import axios from "axios";
import CryptoJS from "crypto-js";
import { toast } from "react-toastify"; // ✅ added for toast

// Secret key for encryption (in production, move this to environment variables)
const SECRET_KEY = "GHxGS@9409354811";

// Encrypt data with AES + IV
export const encryptData = (data) => {
  const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
  const iv = CryptoJS.lib.WordArray.random(16); // 16-byte IV

  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
  const cipherBase64 = CryptoJS.enc.Base64.stringify(encrypted.ciphertext);

  return btoa(ivBase64 + "::" + cipherBase64);
};

// Decrypt data
export const decryptData = (encrypted) => {
  try {
    if (!encrypted || typeof encrypted !== "string") {
      throw new Error("Encrypted data is invalid or empty");
    }

    const combined = atob(encrypted);
    const parts = combined.split("::");
    if (parts.length !== 2) {
      throw new Error("Encrypted data format is invalid");
    }

    const iv = CryptoJS.enc.Base64.parse(parts[0]);
    const ciphertext = CryptoJS.enc.Base64.parse(parts[1]);
    const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);

    const decrypted = CryptoJS.AES.decrypt({ ciphertext }, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    if (!decryptedText) throw new Error("Failed to decrypt");

    return JSON.parse(decryptedText);
  } catch (error) {
    console.error("Decryption error:", error.message);
    console.error("Encrypted input:", encrypted);
    return null;
  }
};

// Get token from localStorage with decryption
export const getToken = () => {
  const encryptedToken = localStorage.getItem("token");
  return encryptedToken;
};

// API URLs
export const baseURL = "https://gharena-house.gsdev.in/master_app_api/v13/";
export const apiUrl = `${baseURL}/adminpanel_api/`;
export const imageBaseUrl = `${baseURL}/uploads`;
// export const imageBaseUrl = `${baseURL}/uploads`;

// export const apiUrl = `http://localhost/admin-panel/backend/adminpanel_api`;
// export const imageBaseUrl = `http://localhost/admin-panel/backend/uploads`;

const proxy = "https://cors-anywhere.herokuapp.com/";

// Axios instance
const apiClient = axios.create({
  baseURL: apiUrl,
});

// Request Interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    const adminUser = JSON.parse(localStorage.getItem("user") || "null");
    const userId = adminUser?.id ;
    const userName = adminUser?.name || "";


    if (config.data instanceof FormData) {
      const formFields = {};
      const imageFiles = [];

      for (let [key, value] of config.data.entries()) {
        if (value instanceof File) {
          // Generate unique file name (same way you do in AddCustomer)
          const timestamp = Math.floor(Date.now() / 1000);
          const randomNum = Math.floor(100000 + Math.random() * 900000);
          const ext = value.name.split(".").pop() || "jpg";
          const uniqueFileName = `kanchan_${timestamp}_${randomNum}.${ext}`;

          // Add only the filename in encrypted input
          formFields[key] = uniqueFileName;

          // Store file info for appending later
          imageFiles.push({ key, file: value, newName: uniqueFileName });
        } else {
          formFields[key] = value;
        }
      }

      // Build encrypted request body
      const requestData = {
        authentication: token || "",
        input: {
          ...formFields,
          by_user: userId || "",
          by_user_name: userName || "",
        },
      };
      console.log("requestData1", requestData);
      const encodedData = encryptData(requestData);
      console.log("encodedData1", encodedData);
      // New FormData with encrypted data + renamed files
      const newFormData = new FormData();
      newFormData.append("data", encodedData);

      imageFiles.forEach(({ key, file, newName }) => {
        // Append file but override the filename sent
        newFormData.append(key, file, newName);
      });

      config.data = newFormData;
    } else {
      // Regular JSON data
      const requestData = {
        authentication: token || "",
        input: {
          ...config.data,
          by_user: userId,
          by_user_name: userName,
        },
      };
      console.log("requestData2", requestData);
      const encodedData = encryptData(requestData);
      console.log("encodedData2", encodedData);
      config.data = { data: encodedData };
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ prevent multiple redirects firing simultaneously
let isRedirectingForExpiry = false;

// Response Interceptor — only added handling for isValidToken
apiClient.interceptors.response.use(
  (response) => {
    const body = response?.data;
    console.log("body", body);

    if (body && typeof body === "object") {
      const tokenFailedMessage = typeof body.message === "string" ? body.message.toLowerCase() : "";
      const isNewExpiredFormat = body.success === false &&
        (tokenFailedMessage.includes("token verification failed") || tokenFailedMessage.includes("expired"));

      if (body.isValidToken === 1 || isNewExpiredFormat) {
        if (!isRedirectingForExpiry) {
          isRedirectingForExpiry = true;
          try {
            // Open global modal instead of immediate redirect
            triggerSessionExpire(
              body.message || "Your session has expired. Please log in again."
            );
          } catch {}
        }
        // stop callers from using this response
        return Promise.reject(new Error(body.message || "Token expired"));
      }
    }

    return response;
  },
  (error) => {
    // keep your original behavior: no extra changes here
    return Promise.reject(error);
  }
);
let onSessionExpire = null;
export const setSessionExpireHandler = (handler) => {
  onSessionExpire = handler;
};
export const triggerSessionExpire = (message) => {
  if (typeof onSessionExpire === "function") {
    onSessionExpire(message);
  }
};
// Auth Token Functions
export const clearAuthToken = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("keepLoggedIn");
  localStorage.removeItem("permissions");
  localStorage.removeItem("user");
};

export const setAuthToken = (token, keepLoggedIn = false) => {
  if (!token) {
    console.warn("No token to store.");
    return;
  }

  const encrypted = encryptData(token);
  localStorage.setItem("token", encrypted);
  console.log("Token encrypted and stored:", encrypted);

  if (keepLoggedIn) {
    localStorage.setItem("keepLoggedIn", "true");
  }
};

// User Data Functions
export const setUserData = (data) => {
  if (data) {
    localStorage.setItem("userData", encryptData(data));
  }
};

export const getUserData = () => {
  const encryptedData = localStorage.getItem("userData");
  return encryptedData ? decryptData(encryptedData) : null;
};

// User Permissions Functions
export const setUserPermissions = (permissions) => {
  if (permissions) {
    localStorage.setItem("userPermissions", encryptData(permissions));
  }
};

export function getUserPermissions() {
  const encryptedPermissions = localStorage.getItem("permissions");
  if (!encryptedPermissions) return null;

  const decrypted = decryptData(encryptedPermissions);
  if (!decrypted) {
    console.warn("Permissions decryption failed");
    return null;
  }

  return decrypted;
}

// Token Expiry (JWT)
export const getTokenExpiry = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const payload = token.split(".")[1];
    const decodedPayload = JSON.parse(atob(payload));
    return decodedPayload.exp ? new Date(decodedPayload.exp * 1000) : null;
  } catch (error) {
    return null;
  }
};

// Utility Date Formatter
export const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}-${month}-${year}`;
};

export { apiClient };
